import React, { useEffect, useLayoutEffect, Suspense } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { authRoutes, publicRoutes, ForcedChangePassword } from "./router.link";
import Feature from "../feature";
import AuthFeature from "../authFeature";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { all_routes } from "./all_routes";
import Unauthorized from "../../core/common/unauthorized";
import { SectionLoader } from "../../core/common/loader/FullScreenLoader";
import { checkModulePermission } from "../../core/utils/permissions";
import { getAuthToken } from "../../core/data/interface/authStorage";
import * as bootstrap from "bootstrap";

// Tenancy §3.2/§7.1: routes gated on isPlatformAdmin instead of a Permissions
// V2 key — platform-admin is not a grantable permission, so these paths are
// deliberately absent from routeToModuleMap below (an absent moduleName means
// shouldShowRoute always denies, which is why this needs its own bypass in
// ProtectedRoute rather than relying on that lookup).
const PLATFORM_ADMIN_ONLY_ROUTES: string[] = [
  all_routes.tenants,
  all_routes.addTenant,
  all_routes.tenantUsers,
  all_routes.tenantModules,
  all_routes.tenantShares,
  all_routes.tenantDataReplication,
  all_routes.tenantDataCopy,
];

// Account pages any signed-in user (platform admins included) may open.
const PLATFORM_ADMIN_ACCOUNT_ROUTES: string[] = [
  all_routes.profile,
  all_routes.forcedChangePassword,
];

// routeToModuleMap maps route keys to one or more module names returned by the backend.
// It accepts either a single string or an array of acceptable module names to make
// matching robust across deployments where module names may vary slightly.
const routeToModuleMap: { [key: string]: string | string[] } = {
  dealsDashboard: "dashboard",
  leadsDashboard: "dashboard",
  projectDashboard: "dashboard",
  contactList: "employees",
  addEmployee: "employees",
  editEmployee: "employees",
  EmployeeTypes: "Employee-Types",
  teamList: "teams",
  customers: "customers",
  addCustomer: "customers",
  editCustomer: "customers",
  farmList: "farms",
  addFarm: "farms",
  editFarm: "farms",
  auditType: "auditType",
  audit: "audit",
  auditDetails: "audit",
  auditReport: "auditReport",
  auditReportSubmissions: "auditReportSubmissions",
  customerRequestReport: "customerRequestReport",
  allReports: "All Reports",
  submissionReports: "Submission Reports",
  rolesPermissions: "roles",
  permissions: "roles",
  permissionsV2: "roles",
  roleVisibility: "roles",
  calendarVisibility: "roles",
  attendance: "attendance",
  vanPhoto: "van photo",
  teamDetails: "teams",
  HR: "HR",
  ExtraWages: "Extra Wages",
  Volunteer: "Volunteer",
  ClothingItem: "ClothingItem",
  ClothingRequest: "ClothingRequest",
  ClothingReturn: "ClothingReturn",
  HolidayRequest: "HolidayRequest",
  ContractType: "Contract Type",
  Department: "Departments",
  EmployeeCompany: "Employee Companies",
  LeaveType: "Leave Types",
  customerPermissions: "CustomerPermissions",
  ManageCustomerPermissions: "CustomerPermissions",
  Vehicles: "Vehicles",
  DriverTripReport: "DriverTripReport",
  DriverTripReportSettings: "DriverTripReportSettings",
  Machines: "Machines",
  MachineAssignments: "Machine Assignments",
  nonConformancesReport: "Non Conformances Report",
  ncrSubmission: "Non Conformances Report Submissions",
  sanitizedReports: "Sanitized Reports",
  kanbanview: "kanbanview",
  Certifications: "Certifications",
  certificateTypes: "Certificate Types",
  importantDocs: "Important Docs",
  NotificationType: "NotificationType",
  NotificationSettings: "NotificationSettings",
  NotificationTemplate: "NotificationTemplate",
  NotificationTemplatePermissions: "NotificationTemplate",
  SystemReport: "SystemReport",
  CheckInOutSettings: "Check In Out Settings",
  SystemSettings: "System Setting",
  TrackingList: "TrackingList",
  areas: "Area",

  sopList: "sop",
  sopCreate: "sop",
  sopEdit: "sop",
  sopDetails: "sop",

  // Training module routes should map to the Training module (separate from SOP)
  trainingList: "Trainings",
  trainingCreate: "Trainings",
  trainingEdit: "Trainings",
  trainingQuestions: "Trainings",
  trainingSlides: "Trainings",
  trainingResults: "Trainings",
  trainingCategories: "Training Categories",

  // Training Assignments is a separate module and must have its own permission
  trainingAssignments: "Training Assignments",

  myTraining: "myTraining",
  trainingPlayer: "myTraining",
  sopAssign: "Training Assignments",
  trainingDashboard: "Training Dashboard",


  // Training Record Request Module Routes
  submitAuditDocRequest: "Audit Doc Request",
  auditDocRequestDashboard: "Training Record Request",
  trainingRecordRequestDetail: "Training Record Approval",
  // Incident Report
  incidentReport: "Incident Report",

  // Auditor Timesheet
  auditorTimesheet: "Auditing Clock In/Out Times",
  shiftList: "Shift Type",
  SiteType: "Site Type",

  // Audit Escalations
  auditEscalations: "auditEscalations",

  // Employee Induction — records routes need induction.records.*; the template
  // settings routes are split out to their own module so induction.templates.manage
  // is required independently (matches the sidebar's "Induction Templates" row).
  inductionList: "Employee Induction",
  inductionPending: "Employee Induction",
  inductionDetail: "Employee Induction",
  inductionSettings: "Induction Templates",
  inductionCreateTemplate: "Induction Templates",
  customEmail: "Custom Email",
  customWhatsApp: "Custom WhatsApp",
};

const publicAccessRoutes = [
  all_routes.leadsKanban,
  all_routes.dealsKanban,
  all_routes.estimationKanban,
  all_routes.profile,
  all_routes.AllNotifications,
  all_routes.interactiveElearningDemo,
];

// Helper function to get first accessible route for user
const getFirstAccessibleRoute = (permissionKeys: string[]): string => {
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
    return all_routes.profile;
  }

  // Check dashboard first (Permissions V2 — key-based)
  if (checkModulePermission("dashboard", "canAccess")) {
    return all_routes.dealsDashboard;
  }

  // Define route priority order based on common usage
  const routePriority = [
    { route: all_routes.contactList, module: "employees" },
    { route: all_routes.addEmployee, module: "employees" },
    { route: all_routes.customers, module: "customers" },
    { route: all_routes.farmList, module: "farms" },
    { route: all_routes.teamList, module: "teams" },
    { route: all_routes.attendance, module: "attendance" },
    { route: all_routes.allReports, module: "All Reports" },
    { route: all_routes.audit, module: "audit" },
    { route: all_routes.vanPhoto, module: "van photo" },
    { route: all_routes.Vehicles, module: "Vehicles" },
    { route: all_routes.Machines, module: "Machines" },
    { route: all_routes.MachineAssignments, module: "Machine Assignments" },
    { route: all_routes.rolesPermissions, module: "roles" },
  ];

  // Find first accessible route (key-based)
  for (const item of routePriority) {
    if (checkModulePermission(item.module, "canAccess")) {
      return item.route;
    }
  }

  // Fallback to profile if no accessible routes found
  return all_routes.profile;
};

const shouldShowRoute = (
  path: string,
  permissionKeys: string[],
) => {
  if (publicAccessRoutes.includes(path)) {
    return true;
  }

  // Profile is always accessible
  if (path === all_routes.profile) {
    return true;
  }

  if (path === all_routes.ManageCustomerPermissions) {
    return checkModulePermission('customers', 'canEdit');
  }

  if (path.startsWith(all_routes.teamDetails.split(':')[0])) {
    return checkModulePermission('teams', 'canEdit');
  }

  // Permissions V2 — access is decided from the granted key set.
  const hasAnyPermissions = Array.isArray(permissionKeys) && permissionKeys.length > 0;

  if (!hasAnyPermissions) {
    // Only allow profile access when user has no permissions
    return path === all_routes.profile;
  }

  const routeKey = Object.entries(all_routes).find(
    ([_, routePath]) => routePath === path
  )?.[0];

  if (!routeKey) return false;

  const moduleName = routeToModuleMap[routeKey];

  if (!moduleName) return false;

  // Support either a single module name or an array of possible module names.
  // checkModulePermission resolves each to its V2 key prefix.
  const moduleNames = Array.isArray(moduleName) ? moduleName : [moduleName];

  return moduleNames.some((n: any) => checkModulePermission(n, "canAccess"));
};

const ProtectedRoute = ({
  element: Element,
  path,
}: {
  element: React.ReactNode;
  path: string;
}) => {
  const permissionKeys = useSelector((state: any) => state.permissions?.permissionKeys);
  // Tenancy §3.2/§7.1: platform-admin routes are gated on this claim, not a
  // Permissions V2 key (platform-admin is deliberately not a grantable
  // permission — see TenantList.tsx's own doc comment).
  const isPlatformAdmin = useSelector((state: any) => state.auth?.user?.isPlatformAdmin);
  const isAuthenticated = !!getAuthToken();
  if (!isAuthenticated) {
    return <Navigate to={all_routes.login} replace />;
  }

  if (authRoutes.some((route) => route.path === path)) {
    return <>{Element}</>;
  }

  if (PLATFORM_ADMIN_ONLY_ROUTES.includes(path)) {
    return isPlatformAdmin ? <>{Element}</> : <Unauthorized />;
  }

  // The user's own account pages hold no company data, so a platform admin can open
  // them too ("My Profile" in the header used to bounce them back to Companies).
  if (isPlatformAdmin && PLATFORM_ADMIN_ACCOUNT_ROUTES.includes(path)) {
    return <>{Element}</>;
  }

  // Tenancy §3.2 / §7.1: a platform (super) admin has no tenant data — every
  // non-platform route is off-limits. Send them back to company management.
  if (isPlatformAdmin) {
    return <Navigate to={all_routes.tenants} replace />;
  }

  if (shouldShowRoute(path, permissionKeys)) {
    return <>{Element}</>;
  }

  return <Unauthorized />;
};

const ALLRoutes: React.FC = () => {
  const location = useLocation();

  // Reset scroll before the route (or its lazy-loading fallback) is painted.
  // Without this, long pages such as System Report briefly render at the
  // previous page's bottom scroll position and visibly jump to the top later.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);
  const isAuthenticated = !!getAuthToken();
  const permissionKeys = useSelector((state: any) => state.permissions?.permissionKeys);
  // Tenancy §3.2/§7.1: a platform (super) admin holds no permission keys by
  // design, so the no-permissions guard below must exempt them (same exemption
  // feature.tsx applies to its own check).
  const isPlatformAdmin = useSelector((state: any) => state.auth?.user?.isPlatformAdmin);

  const matchRoute = (routePath: string, currentPath: string) => {
    const routeParts = routePath.split("/");
    const pathParts = currentPath.split("/");

    if (routeParts.length !== pathParts.length) return false;

    for (let i = 0; i < routeParts.length; i++) {
      if (routeParts[i].startsWith(":")) continue;
      if (routeParts[i] !== pathParts[i]) return false;
    }

    return true;
  };

  const currentRoute =
    publicRoutes.find((route) => matchRoute(route.path, location.pathname)) ||
    authRoutes.find((route) => matchRoute(route.path, location.pathname));

  const fullTitle = currentRoute?.title
    ? `${currentRoute.title} | CM-Central`
    : "";

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const dropdownElementList = document.querySelectorAll('[data-bs-toggle="dropdown"]');
      Array.from(dropdownElementList).forEach(dropdownToggleEl => {
        if (!bootstrap.Dropdown.getInstance(dropdownToggleEl)) {
          new bootstrap.Dropdown(dropdownToggleEl);
        }
      });

      const modalElementList = document.querySelectorAll('[data-bs-toggle="modal"]');
      Array.from(modalElementList).forEach(modalToggleEl => {
        if (!bootstrap.Modal.getInstance(modalToggleEl)) {
          new bootstrap.Modal(modalToggleEl);
        }
      });

      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      Array.from(tooltipTriggerList).forEach(tooltipTriggerEl => {
        if (!bootstrap.Tooltip.getInstance(tooltipTriggerEl)) {
          new bootstrap.Tooltip(tooltipTriggerEl);
        }
      });

      const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
      Array.from(popoverTriggerList).forEach(popoverTriggerEl => {
        if (!bootstrap.Popover.getInstance(popoverTriggerEl)) {
          new bootstrap.Popover(popoverTriggerEl);
        }
      });
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]);

  // Always redirect root path to dashboard first.
  // This prevents '/' from being blocked by permission guards before redirecting.
  if (location.pathname === "/") {
    return <Navigate to={all_routes.dealsDashboard} replace />;
  }

  // Permissions V2 — a signed-in user with no granted keys has no access at all.
  const hasAnyPermissions = Array.isArray(permissionKeys) && permissionKeys.length > 0;
  // Admin-shared credentials must be changed before anything else in the app
  // is reachable. Locked entirely on the frontend — see forcedChangePassword.
  const mustChangePassword = isAuthenticated && localStorage.getItem("mustChangePassword") === "true";
  if (mustChangePassword && location.pathname !== all_routes.forcedChangePassword) {
    return <Navigate to={all_routes.forcedChangePassword} replace />;
  }

  // Don't block the forced-change-password page itself on permissions — a
  // brand-new tenant admin has just logged in with generated credentials and
  // may not have fetched/received permissionKeys yet; they still need to
  // reach this page to set their real password.
  //
  // Tenancy §3.2/§7.1: a platform (super) admin legitimately holds ZERO
  // permission keys — the backend deliberately grants them no roles and no
  // TenantUser row — but is NOT unauthorized. Without this bypass every
  // platform-admin path rendered <Unauthorized />, even once the routes exist.
  // feature.tsx already applies exactly this exemption to its own
  // hasAnyPermissions check; this guard was simply never given the same one.
  //
  // The invitation page is answered with the emailed token, not the session, so
  // it stays reachable whatever this session's permissions are.
  if (
    isAuthenticated &&
    !hasAnyPermissions &&
    !isPlatformAdmin &&
    location.pathname !== all_routes.forcedChangePassword &&
    location.pathname !== all_routes.acceptInvite
  ) {
    return <Unauthorized />;
  }

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
      </Helmet>
      <Suspense fallback={null}>
        <Routes>
          {/* Standalone, chrome-less route — rendered outside both AuthFeature
              and Feature so no sidebar/header leaks in while it's forced. */}
          <Route
            path={all_routes.forcedChangePassword}
            element={
              isAuthenticated ? (
                <Suspense fallback={<SectionLoader isLoading message="Loading..." />}>
                  <ForcedChangePassword />
                </Suspense>
              ) : (
                <Navigate to={all_routes.login} replace />
              )
            }
          />

          <Route element={<AuthFeature />}>
            {authRoutes.map((route) => {
              // Platform admins hold no permission keys, so the key-based pick would
              // send them to Profile, and this redirect would override the login's own
              // navigation to company management.
              const redirectPath = isPlatformAdmin ? all_routes.tenants : getFirstAccessibleRoute(permissionKeys);

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    isAuthenticated ? (
                      <Navigate to={redirectPath} replace />
                    ) : (
                      <Suspense
                        fallback={
                          <SectionLoader isLoading message="Loading..." />
                        }
                      >
                        {route.element}
                      </Suspense>
                    )
                  }
                />
              );
            })}
          </Route>

          {isAuthenticated ? (
            <Route element={<Feature />}>
              {publicRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      element={
                        <Suspense
                          fallback={
                            <SectionLoader isLoading message="Loading..." />
                          }
                        >
                          {route.element}
                        </Suspense>
                      }
                      path={route.path}
                    />
                  }
                />
              ))}
            </Route>
          ) : (
            <Route path="*" element={<Navigate to={all_routes.login} replace />} />
          )}

          <Route
            path="/"
            element={<Navigate to={all_routes.dealsDashboard} replace />}
          />

          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate
                  to={isPlatformAdmin ? all_routes.tenants : getFirstAccessibleRoute(permissionKeys)}
                  replace
                />
              ) : (
                <Navigate to={all_routes.login} replace />
              )
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};

export default ALLRoutes;
